import omit from "lodash/omit.js";

import groupBy from "lodash/groupBy.js";

import type { TStateTableMappings } from "./sliceTableMappings";
import type { TStateTableExtractions } from "../tableExtractions/sliceTableExtractions";
import type { TDtoTableExtraction } from "../tableExtractions/TDtoTableExtraction";
import type { TOptionWithType } from "../accounts/accountTypes";

export const createSelectorTableMappingsWithAppliedChangesAndOmittedRows =
    () =>
    (state: {
        rowsExtracted: TStateTableExtractions["extractionRowsLoaded"];
        rowsOmitted: TStateTableMappings["rowsOmitted"];
        rowsWithMappingsAltered: TStateTableMappings["rowsWithMappingsAltered"];
        optionsAccounts: TOptionWithType[];
    }): TDtoTableExtraction | null => {
        const { rowsExtracted, rowsWithMappingsAltered, rowsOmitted } = state;

        if (rowsExtracted === null) return null;

        const newRows = rowsExtracted.rows.map(row => {
            const isRowAlreadyOmitted = rowsOmitted.some(
                omittedRow => omittedRow.rowId === row.rowId,
            );
            if (isRowAlreadyOmitted) {
                return {
                    ...row,
                    account: null, // account should not have a value, because if it is omitted
                    mappingType: "omit" as const,
                };
            }

            const rowChanges = rowsWithMappingsAltered.find(
                change => change.rowId === row.rowId,
            );
            if (!rowChanges) {
                return row;
            }
            const newRow = {
                ...row,
            };
            if (rowChanges.type !== undefined) {
                newRow["type"] = rowChanges.type;
            }
            if (rowChanges.account !== undefined) {
                const accountWithType = state.optionsAccounts.find(
                    account => account.value === rowChanges.account,
                );
                newRow.account = rowChanges.account;
                newRow.mappingType = "mapped" as const;
                if (accountWithType) {
                    newRow.currentSectionType = accountWithType.type;
                }
            }
            return newRow;
        });

        return {
            ...rowsExtracted,
            rows: newRows,
        };
    };

type ErrorType = "missingMapping" | "dataOutlier" | "llmValidation";

export type ErrorRow = {
    rowId: number | string;
    colId: string;
    periodId?: number;
    columnName?: string;
    accountName: string;
    errorType: ErrorType;
    errorValue?: number;
    severity: "warning" | "error";
    threshold?: number;
    direction?: "small" | "large";
    resolved?: boolean;
    dismissed?: boolean;
    checkType?: string;
    expected?: string;
    actual?: string;
    diff?: string;
};

export type TRowTotalNetIncome = {
    [key: string]: string | number | boolean | null;
    rowId: number;
    account: string | null;
    originalSectionType: "income" | "expense" | "non-operating";
    flipRowSign: boolean | null;
};

export type TOptionsAccount = {
    accountCode: string;
    accountType: "income" | "expense" | "non-operating";
};

export const createSelectorTableMappingsErrorRows =
    () =>
    ({
        rowsLoaded,
        rowsLoadedWithChangesApplied,
    }: {
        rowsLoaded: TDtoTableExtraction | null;
        rowsLoadedWithChangesApplied: TDtoTableExtraction | null;
    }): { [key: string]: ErrorRow[] } => {
        if (!rowsLoaded) return {};

        const rowsLoadedWithoutMapping = rowsLoaded.rows.filter(
            row => row.mappingType === "missing",
        );

        const errors = rowsLoadedWithoutMapping.map(row => {
            const sameRowAfterChangesApplied =
                rowsLoadedWithChangesApplied?.rows.find(
                    rowAfterChangesApplied =>
                        rowAfterChangesApplied.rowId === row.rowId,
                );
            const isRowResolved = sameRowAfterChangesApplied
                ? sameRowAfterChangesApplied.mappingType !== "missing"
                : false;

            return {
                id: row.rowId,
                rowId: row.rowId,
                colId: "account",
                accountName: row.originalAccountName,
                errorCategory: "error" as const,
                errorType: "missingMapping" as const,
                severity: "error" as const,
                resolved: isRowResolved,
            };
        });

        const errorGroups = groupBy(errors, error => {
            return [error.rowId, error.severity];
        });

        return errorGroups;
    };

export const createSelectorTableMappingsTotalNetIncome =
    () =>
    ({
        rows,
        optionsAccounts,
        isOriginalTotalNetIncome,
    }: {
        rows: TRowTotalNetIncome[];
        optionsAccounts: TOptionsAccount[];
        isOriginalTotalNetIncome: boolean;
    }): { [key: string]: number } => {
        const totalNetIncome = rows.reduce<{ [key: string]: number }>(
            (acc, row) => {
                const accOperatingStatements = omit(acc, [
                    "rowId",
                    "originalAccountName",
                    "account",
                ]);
                const operatingStatements = omit(row, [
                    "rowId",
                    "originalAccountName",
                    "account",
                ]);

                const rowAccount = optionsAccounts.find(
                    account => account.accountCode === row.account,
                );
                if (!isOriginalTotalNetIncome && !rowAccount) return acc;
                const rowType = isOriginalTotalNetIncome
                    ? row.originalSectionType
                    : rowAccount?.accountType;

                const isRowIncome = rowType === "income";
                const isRowExpense = rowType === "expense";

                const newAccOperatingStatements = Object.entries(
                    operatingStatements,
                ).reduce<{ [key: string]: number }>((acc, [key, value]) => {
                    if (typeof value !== "number") return acc;

                    // Adjust for flipped row signs. These are applied to cells
                    // in the back-end, so we need to adjust for them when
                    // calculating the original NOI
                    const adjusted =
                        isOriginalTotalNetIncome && (row.flipRowSign ?? false)
                            ? -value
                            : value;

                    const valueToSum = isRowIncome
                        ? adjusted
                        : isRowExpense
                          ? -adjusted
                          : 0;

                    acc[key] =
                        acc[key] === undefined
                            ? valueToSum
                            : acc[key] + valueToSum;

                    return acc;
                }, accOperatingStatements);

                return {
                    ...newAccOperatingStatements,
                };
            },
            {} as { [key: string]: number },
        );

        return totalNetIncome;
    };
